import { DataTable } from "@/components/ui/data-table";
import { formatDate } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { AddOrderDialog } from "./dialogs/AddOrderDialog";
import { SupplierActions } from "./actions/SupplierActions";
import { RatingCell } from "./table/RatingCell";
import { OrdersCell } from "./table/OrdersCell";
import { Supplier } from "./types";

interface SupplierTableProps {
  suppliers: Supplier[];
  refetch: () => void;
}

export const SupplierTable = ({ suppliers, refetch }: SupplierTableProps) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [isAddOrderDialogOpen, setIsAddOrderDialogOpen] = useState(false);

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("suppliers")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Fournisseur supprimé avec succès");
      refetch();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.error("Erreur lors de la suppression du fournisseur");
    }
  };

  const handleAddOrder = (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setIsAddOrderDialogOpen(true);
  };

  const columns = [
    {
      id: "name",
      label: "Nom",
      cell: (row: Supplier) => row.name,
    },
    {
      id: "contact",
      label: "Contact",
      cell: (row: Supplier) => row.contact_person || "-",
    },
    {
      id: "phone",
      label: "Téléphone",
      cell: (row: Supplier) => row.phone || "-",
    },
    {
      id: "email",
      label: "Email",
      cell: (row: Supplier) => row.email || "-",
    },
    {
      id: "category",
      label: "Catégorie",
      cell: (row: Supplier) => (
        <Badge variant="secondary">{row.category || "Non catégorisé"}</Badge>
      ),
    },
    {
      id: "rating",
      label: "Note",
      cell: (row: Supplier) => <RatingCell ratings={row.supplier_ratings} />,
    },
    {
      id: "orders",
      label: "Commandes",
      cell: (row: Supplier) => (
        <OrdersCell 
          orders={row.supplier_orders} 
          supplierId={row.id}
          onUpdate={refetch}
        />
      ),
    },
    {
      id: "created_at",
      label: "Date d'ajout",
      cell: (row: Supplier) => formatDate(row.created_at),
    },
    {
      id: "actions",
      label: "Actions",
      cell: (row: Supplier) => (
        <SupplierActions
          supplier={row}
          onAddOrder={handleAddOrder}
          onDelete={() => handleDelete(row.id)}
          onUpdate={refetch}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable columns={columns} data={suppliers} />
      {selectedSupplierId && (
        <AddOrderDialog
          open={isAddOrderDialogOpen}
          onOpenChange={setIsAddOrderDialogOpen}
          onSuccess={refetch}
          supplierId={selectedSupplierId}
        />
      )}
    </>
  );
};